import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { fetchProducts } from '../services/api';

const STALE_TIME = 1 * 60 * 1000; // 1 minute

interface UseProductsFilters {
  catalogId?: string;
  categoryId?: string;
  search?: string;
  currency?: string;
}

/**
 * Hook to fetch products with filters (non-infinite)
 * @param filters - Product filters
 */
export function useProducts(filters: UseProductsFilters = {}) {
  const { catalogId, categoryId, search, currency = 'GBP' } = filters;

  return useQuery({
    queryKey: ['products', { catalogId, categoryId, search, currency }],
    queryFn: () =>
      fetchProducts({
        catalogId,
        categoryId,
        search,
        currency,
        limit: 20,
      }),
    staleTime: STALE_TIME,
    enabled: !!catalogId || !!search,
  });
}

/**
 * Hook to fetch products with infinite scroll support
 * @param filters - Product filters
 */
export function useInfiniteProducts(filters: UseProductsFilters = {}) {
  const { catalogId, categoryId, search, currency = 'GBP' } = filters;

  return useInfiniteQuery({
    queryKey: ['products', 'infinite', { catalogId, categoryId, search, currency }],
    queryFn: ({ pageParam }) =>
      fetchProducts({
        catalogId,
        categoryId,
        search,
        currency,
        limit: 20,
        cursor: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.pagination.cursor,
    staleTime: STALE_TIME,
    enabled: !!catalogId || !!search,
  });
}

/**
 * Hook to search products
 * @param searchTerm - Search term (undefined disables query)
 * @param catalogId - Optional catalog to search within
 */
export function useProductSearch(searchTerm: string | undefined, catalogId?: string) {
  return useQuery({
    queryKey: ['products', 'search', { searchTerm, catalogId }],
    queryFn: () =>
      fetchProducts({
        search: searchTerm,
        catalogId,
        currency: 'GBP',
        limit: 20,
      }),
    staleTime: STALE_TIME,
    enabled: searchTerm !== undefined && searchTerm.trim().length > 0,
  });
}
